import React, { createContext, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Polyglot from 'node-polyglot';
import Logger from '@braincube/logger';
import pkg from '../../../package.json';

/**
 * React context to store I18n functionnality
 * @type {React.Context<{}>}
 */
const I18nContext = /*#__PURE__*/createContext({});
const useI18n = () => useContext(I18nContext);

/**
 * Push in the I18nContext a Polyglot instance with the application label set.
 * Context consumer will receive an object containing method to translate text keys into the given locale.
 */
const I18nProvider = ({
  locale,
  labels,
  onMissingKey,
  children
}) => {
  const existingContext = useI18n();
  useEffect(() => {
    if (Object.keys(existingContext).length > 0) {
      Logger.warn('You should not have multiple I18nProviders in the same application', {
        library: `${pkg.name}@${pkg.version}`
      });
    }
  }, [existingContext]);
  const i18n = useMemo(() => {
    const translator = new Polyglot({
      locale,
      phrases: labels,
      allowMissing: false,
      onMissingKey: (key, ...rest) => {
        Logger.warn(`Missing translation for key ${key}`, {
          library: `${pkg.name}@${pkg.version}`,
          ...rest
        });
        if (typeof onMissingKey === 'function') {
          return onMissingKey(key, ...rest);
        }
        return onMissingKey === null ? key : onMissingKey;
      }
    });

    /**
     * Capitalize a word (transform its first letter to upper case)
     * @param {String} word A word.
     * @return {String} The word capitalized.
     * @private
     */
    const capitalizeWord = word => {
      if (word.length > 0) {
        return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
      }
      return '';
    };

    /**
     * Return the label for a given key. You can provide variables as a second parameter to perform string interpolation.
     * For example, `getTranslation('hello', { name: 'Spike' });` returns "Hello, Spike" if label `hello` is `Hello, %{name}`.
     * You can also control singular or plural by providing the special `smart_count` variable (it will be used for choosing the
     * singular / plural label, even if it not displayed).
     * @param {String} key Label key.
     * @param {Object} substitutions Substitution variables to use for interpolation.
     * @param {Function} transformer Function to apply before returning the label. It takes a label as a parameter.
     */
    const getTranslation = (key, substitutions = {}, transformer = text => text) => {
      // Remove the _ option of polyglot: we do not want default labels for each call. We prefer using onMissingKey.
      // eslint-disable-next-line no-param-reassign
      delete substitutions._;
      const textBeforeTransform = translator.t(key, substitutions);
      return transformer(textBeforeTransform);
    };

    /**
     * Return the label for a given key, then transform capitalize it (the first letter in upper case).
     * @param {String} key Label key.
     * @param {Object} substitutions Substitution variables to use for interpolation.
     * @see getTranslation
     */
    const getTranslationCapitalized = (key, substitutions = {}) => {
      return getTranslation(key, substitutions, capitalizeWord);
    };

    /**
     * Returns a boolean indicating whether or not the given key exists in the translations
     * @param {String} key Label key.
     */
    const hasTranslationKey = key => {
      return translator.has(key);
    };
    return {
      t: getTranslation,
      tc: getTranslationCapitalized,
      has: hasTranslationKey,
      labels
    };
  }, [locale, labels, onMissingKey]);
  return /*#__PURE__*/React.createElement(I18nContext.Provider, {
    value: i18n
  }, children);
};
I18nProvider.propTypes = {
  /** A string describing the locale of the translation, to apply pluralization rules.
   * Must respect Polyglot locale format (You can use getLocaleKey from localesTranslator to be compatible).
   */
  locale: PropTypes.string.isRequired,
  /** Set of labels for the locale. */
  labels: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  /**
   * If set, this function is called when a key cannot be found. It is passed the key, the options, the locale and an object with pluralization rules as
   * parameters. It has to return the label to display.
   * If not set, a warning is issued in console and the key is displayed.
   */
  onMissingKey: PropTypes.func
};
I18nProvider.defaultProps = {
  onMissingKey: null
};
export { I18nContext, I18nProvider, useI18n };